import React, { useEffect, useRef, useState } from 'react'
import { Activity, ChevronLeft, ChevronRight, FileText, FlaskConical, ListChecks, Settings2, Brain, ClipboardCheck } from 'lucide-react'

type WorkspaceTab = 'readiness' | 'configuration' | 'tests' | 'approvals' | 'files' | 'memory' | 'activity'

const tabs: Array<{ id: WorkspaceTab; label: string; icon: React.ReactNode; available: boolean }> = [
  { id: 'readiness', label: 'Readiness', icon: <ListChecks size={14} />, available: true },
  { id: 'configuration', label: 'Configuration', icon: <Settings2 size={14} />, available: true },
  { id: 'tests', label: 'Tests', icon: <FlaskConical size={14} />, available: true },
  { id: 'approvals', label: 'Approvals', icon: <ClipboardCheck size={14} />, available: true },
  { id: 'files', label: 'Files', icon: <FileText size={14} />, available: true },
  { id: 'memory', label: 'Memory', icon: <Brain size={14} />, available: true },
  { id: 'activity', label: 'Activity', icon: <Activity size={14} />, available: false },
]

const WIDTH_KEY = 'saviynt-agent.workspace-panel-width'
const COLLAPSED_KEY = 'saviynt-agent.workspace-panel-collapsed'

export default function WorkspacePanel({ children, configuration, tests, approvals, files, memory }: { children: React.ReactNode; configuration?: React.ReactNode; tests?: React.ReactNode; approvals?: React.ReactNode; files?: React.ReactNode; memory?: React.ReactNode }) {
  const [activeTab, setActiveTab] = useState<WorkspaceTab>('readiness')
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSED_KEY) === 'true')
  const [width, setWidth] = useState(() => {
    const stored = Number(localStorage.getItem(WIDTH_KEY))
    return Number.isFinite(stored) && stored >= 280 && stored <= 520 ? stored : 340
  })
  const [resizing, setResizing] = useState(false)
  const dragStart = useRef<{ x: number; width: number } | null>(null)

  useEffect(() => localStorage.setItem(COLLAPSED_KEY, String(collapsed)), [collapsed])
  useEffect(() => localStorage.setItem(WIDTH_KEY, String(width)), [width])

  useEffect(() => {
    if (!resizing) return
    const move = (event: PointerEvent) => {
      if (!dragStart.current) return
      const next = dragStart.current.width + (dragStart.current.x - event.clientX)
      setWidth(Math.max(280, Math.min(520, next)))
    }
    const stop = () => {
      dragStart.current = null
      setResizing(false)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', stop)
    window.addEventListener('pointercancel', stop)
    return () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', stop)
      window.removeEventListener('pointercancel', stop)
    }
  }, [resizing])

  const startResize = (event: React.PointerEvent<HTMLButtonElement>) => {
    dragStart.current = { x: event.clientX, width }
    setResizing(true)
    event.preventDefault()
  }

  const resizeWithKeyboard = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
    event.preventDefault()
    setWidth(value => Math.max(280, Math.min(520, value + (event.key === 'ArrowLeft' ? 16 : -16))))
  }

  return (
    <aside
      className={`workspace-panel ${collapsed ? 'collapsed' : ''} ${resizing ? 'resizing' : ''}`}
      style={{ '--workspace-panel-width': `${width}px` } as React.CSSProperties}
      aria-label="Connector workspace panel"
    >
      {!collapsed && (
        <button
          type="button"
          className="workspace-panel-resizer"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize workspace panel"
          aria-valuemin={280}
          aria-valuemax={520}
          aria-valuenow={Math.round(width)}
          onPointerDown={startResize}
          onKeyDown={resizeWithKeyboard}
        />
      )}

      <div className="workspace-panel-toolbar">
        {!collapsed && <strong>Workspace</strong>}
        <button
          type="button"
          className="workspace-panel-collapse"
          onClick={() => setCollapsed(value => !value)}
          aria-label={collapsed ? 'Open workspace panel' : 'Collapse workspace panel'}
          title={collapsed ? 'Open workspace panel' : 'Collapse workspace panel'}
        >
          {collapsed ? <ChevronLeft size={15} /> : <ChevronRight size={15} />}
        </button>
      </div>

      {collapsed ? (
        <div className="workspace-panel-rail" aria-hidden="true">Workspace</div>
      ) : (
        <>
          <div className="workspace-panel-tabs" role="tablist" aria-label="Workspace views">
            {tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.id}
                aria-controls={`workspace-tab-${tab.id}`}
                className={activeTab === tab.id ? 'active' : ''}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.icon}<span>{tab.label}</span>
              </button>
            ))}
          </div>
          <div
            id={`workspace-tab-${activeTab}`}
            className={`workspace-panel-content ${activeTab === 'readiness' ? '' : 'workspace-panel-content-inset'}`}
            role="tabpanel"
          >
            {activeTab === 'readiness' ? children : activeTab === 'configuration' && configuration ? configuration : activeTab === 'tests' && tests ? tests : activeTab === 'approvals' && approvals ? approvals : activeTab === 'files' && files ? files : activeTab === 'memory' && memory ? memory : (
              <div className="workspace-panel-placeholder">
                {tabs.find(tab => tab.id === activeTab)?.icon}
                <h3>{tabs.find(tab => tab.id === activeTab)?.label}</h3>
                <p>This workspace view is planned for Agent v2 Phase 2.</p>
                <button type="button" onClick={() => setActiveTab('readiness')}>Return to readiness</button>
              </div>
            )}
          </div>
        </>
      )}
    </aside>
  )
}
